import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { STATS } from "@/data/content";

function Counter({
  value,
  prefix = "",
  suffix = "",
  inView,
}) {
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;

    const duration = 1400;
    const start = performance.now();

    let raf;

    const tick = (t) => {
      const p = Math.min(
        1,
        (t - start) / duration,
      );

      const eased =
        1 - Math.pow(1 - p, 3);

      setN(
        Math.round(eased * value),
      );

      if (p < 1) {
        raf =
          requestAnimationFrame(tick);
      }
    };

    raf =
      requestAnimationFrame(tick);

    return () => {
      if (raf) {
        cancelAnimationFrame(raf);
      }
    };
  }, [inView, value]);

  return (
    <span className="tabular-nums">
      {prefix}
      {n}
      {suffix}
    </span>
  );
}

function StatCard({ stat, index }) {
  const ref = useRef(null);

  const inView = useInView(ref, {
    once: true,
    amount: 0.2,
  });

  return (
    <motion.div
      ref={ref}
      initial={{
        opacity: 0,
        y: 12,
      }}
      whileInView={{
        opacity: 1,
        y: 0,
      }}
      viewport={{
        once: true,
      }}
      transition={{
        duration: 0.5,
        delay: index * 0.05,
      }}
      data-testid={`stat-${index}`}
      className="rounded-2xl bg-white border border-gray-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
    >
      <div className="text-3xl sm:text-4xl font-extrabold font-heading tracking-tight text-[#0F2D52]">
        <Counter
          value={stat.value}
          prefix={stat.prefix}
          suffix={stat.suffix}
          inView={inView}
        />
      </div>

      <div className="mt-2 text-xs sm:text-sm text-gray-500 leading-snug">
        {stat.label}
      </div>
    </motion.div>
  );
}

export default function Stats() {
  return (
    <section
      data-testid="stats-section"
      className="py-20 md:py-24 bg-[#F8FAFC]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {STATS.map((stat, index) => (
            <StatCard
              key={stat.label}
              stat={stat}
              index={index}
            />
          ))}
        </div>

        <p className="text-xs text-gray-400 mt-4">
          *Processing success varies by applicant profile and lender criteria.
        </p>
      </div>
    </section>
  );
}